import {faTriangleExclamation} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {useQueryClient} from '@tanstack/react-query';
import {Form, FormInstance} from 'antd';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {useCatalogInfo} from '../../../../../../../../provider/catalogs/catalogs-hook';
import {useDomainConfig} from '../../../../../../../../provider/domain-config/domain-config-hook';
import {useLocale} from '../../../../../../../../provider/locale/locale-hook';
import {Locale} from '../../../../../../../../provider/locale/localization';
import {useMessage} from '../../../../../../../../provider/message/message-hook';
import {useModal} from '../../../../../../../../provider/modal/modal-hook';
import {useSnackbar} from '../../../../../../../../provider/snackbar/snackbar-hook';
import {CatalogByGroupType} from '../../../../../../../../service/source-settings/catalogs/catalogs-type';
import {
    invalidateStoreAccountsQueries,
    StoreAccountLinksRowType,
    useAllStoreAccountApps,
    useDeleteStoreAccountMutation,
    useStoreAccountLinks,
} from '../../../../../../../../service/source-settings/store-accounts/store-accounts-hook';
import {classNames} from '../../../../../../../../util/css';
import * as removeAccountStyles from '../remove-account/remove-account.scss';

import {APPSTORE_APPS_LIST_LIMIT, AppstoreAccountStepEnum} from './add-appstore-account-const';
import {
    useBindAppstoreAccountAppsAndFinish,
    useValidateAndCreateAppstoreAccount,
} from './add-appstore-account-form-mutations';
import {
    collectSelectedAppIdsFromForm,
    getDetachedLinkedAppsFromLinks,
    mapStoreAccountLinksRowsToFormValues,
    type StoreAccountDetachedLinkAppType,
} from './add-appstore-account-helper';
import {isAppstoreApiKeyFormComplete, isAppstoreAppsFormComplete} from './add-appstore-form-validation-helper';
import {AppstoreApiKeyFormEnum, AppstoreApiKeyFormType} from './appstore-api-key-form/appstore-api-key-form-type';
import {AppstoreAppsFormEnum, AppstoreAppsFormType} from './appstore-apps-form/appstore-apps-form-type';

const emptyStoreAccountLinks: Array<StoreAccountLinksRowType> = [];

type ArgsType = {
    autoOpen?: boolean;
    catalog: CatalogByGroupType;
    /** When false, open handlers and auto-open are no-ops (e.g. non–App Store catalog in shared table). */
    enabled?: boolean;
    onAutoOpen?: () => void;
    onSuccess: () => void;
};

export type UseAddAppstoreAccountFormReturnType = {
    accountId: number | null;
    apiKeyForm: FormInstance<AppstoreApiKeyFormType>;
    appsForm: FormInstance<AppstoreAppsFormType>;
    appsFormInitialValues: AppstoreAppsFormType;
    bindConfirmDetachedApps: Array<StoreAccountDetachedLinkAppType>;
    currentStep: AppstoreAccountStepEnum;
    handleApiKeyFinish: (values: AppstoreApiKeyFormType) => Promise<void>;
    handleAppsFinish: (values: AppstoreAppsFormType) => Promise<void>;
    handleBindConfirmCancel: () => void;
    handleBindConfirmSave: () => Promise<void>;
    handleClose: () => void;
    handleDeleteAccount: () => void;
    handleOpen: () => void;
    handleOpenForEdit: (storeAccountId: number) => void;
    isBindConfirmOpen: boolean;
    isBindSaving: boolean;
    isEditMode: boolean;
    isInProgress: boolean;
    isModalOpen: boolean;
    isPrefillLoading: boolean;
    isStoreAccountAppsLoading: boolean;
    isStoreAccountLinksLoading: boolean;
    isApiKeyStepSubmittable: boolean;
    isAppsStepSubmittable: boolean;
    linkExtraAppOptions: Array<{appId: string; appName: string}>;
};

/* eslint-disable complexity, max-statements, sonarjs/cognitive-complexity -- orchestrates API key + apps steps and optional bind confirmation */
export function useAddAppstoreAccountForm(args: ArgsType): UseAddAppstoreAccountFormReturnType {
    const {autoOpen = false, catalog, enabled = true, onAutoOpen, onSuccess} = args;

    const {message} = useMessage();
    const {getLocalizedString} = useLocale();
    const queryClient = useQueryClient();
    const {modal} = useModal();
    const {snackbar} = useSnackbar();
    const {companyName} = useDomainConfig();
    const {getCatalogName} = useCatalogInfo();

    const {isLoading: isValidateCreateLoading, validateAndCreate} = useValidateAndCreateAppstoreAccount();
    const {bindAndFinish, isLoading: isBindSaving} = useBindAppstoreAccountAppsAndFinish();
    const {isLoading: isDeleteStoreAccountLoading, mutateAsync: deleteStoreAccountAsync} =
        useDeleteStoreAccountMutation();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [currentStep, setCurrentStep] = useState<AppstoreAccountStepEnum>(AppstoreAccountStepEnum.apiKey);
    const [accountId, setAccountId] = useState<number | null>(null);

    const [isBindConfirmOpen, setIsBindConfirmOpen] = useState(false);
    const [pendingBindValues, setPendingBindValues] = useState<AppstoreAppsFormType | null>(null);
    const [bindConfirmDetachedApps, setBindConfirmDetachedApps] = useState<Array<StoreAccountDetachedLinkAppType>>([]);

    const [apiKeyForm] = Form.useForm<AppstoreApiKeyFormType>();
    const [appsForm] = Form.useForm<AppstoreAppsFormType>();

    const linksHydratedRef = useRef(false);
    const previousLinksSnapshotRef = useRef<Array<StoreAccountLinksRowType>>([]);
    /** When true, strip `openAddAccount` from URL after the add modal fully closes (avoids closing modal on open). */
    const shouldStripAutoOpenQueryRef = useRef(false);

    const issuerId = Form.useWatch(AppstoreApiKeyFormEnum.issuerId, apiKeyForm);
    const keyId = Form.useWatch(AppstoreApiKeyFormEnum.keyId, apiKeyForm);
    const privateKey = Form.useWatch(AppstoreApiKeyFormEnum.privateKey, apiKeyForm);
    const brandApps = Form.useWatch(AppstoreAppsFormEnum.brandApps, appsForm);

    const [apiKeyHasError, setApiKeyHasError] = useState(false);

    useEffect(() => {
        if (!privateKey) {
            setApiKeyHasError(false);

            return;
        }

        apiKeyForm
            .validateFields([AppstoreApiKeyFormEnum.privateKey])
            .then(() => setApiKeyHasError(false))
            .catch((error: {errorFields?: Array<unknown>}) => {
                setApiKeyHasError(Boolean(error?.errorFields?.length));
            });
    }, [apiKeyForm, privateKey]);

    const isApiKeyStepSubmittable = useMemo(
        () =>
            isAppstoreApiKeyFormComplete({
                [AppstoreApiKeyFormEnum.issuerId]: issuerId,
                [AppstoreApiKeyFormEnum.keyId]: keyId,
                [AppstoreApiKeyFormEnum.privateKey]: privateKey,
            }) && !apiKeyHasError,
        [apiKeyHasError, issuerId, keyId, privateKey]
    );

    const isAppsStepSubmittable = useMemo(
        () => isAppstoreAppsFormComplete({[AppstoreAppsFormEnum.brandApps]: brandApps}),
        [brandApps]
    );

    const isAppsStep = currentStep === AppstoreAccountStepEnum.apps;

    const isAppsDataEnabled = Boolean(isModalOpen && accountId !== null && accountId >= 1 && isAppsStep);

    const {isLoading: isStoreAccountAppsLoading} = useAllStoreAccountApps(
        accountId ?? 0,
        {limit: APPSTORE_APPS_LIST_LIMIT},
        {enabled: isAppsDataEnabled}
    );

    const {
        data: linksQueryData,
        isLoading: isLinksQueryLoading,
        isFetching: isLinksQueryFetching,
        isFetched: isLinksFetched,
    } = useStoreAccountLinks(accountId ?? 0, {
        enabled: isAppsDataEnabled,
        ...(isEditMode ? {refetchOnMount: 'always' as const} : {}),
    });

    const storeAccountLinksRows = linksQueryData ?? emptyStoreAccountLinks;

    const isStoreAccountLinksLoading = isLinksQueryLoading || isLinksQueryFetching;

    const linkExtraAppOptions = useMemo(
        () => storeAccountLinksRows.flatMap((row) => row.linkApps),
        [storeAccountLinksRows]
    );

    const appsFormInitialValues = useMemo(
        () => mapStoreAccountLinksRowsToFormValues(storeAccountLinksRows),
        [storeAccountLinksRows]
    );

    const isPrefillLoading = isEditMode && isModalOpen && !isLinksFetched;

    const isInProgress = isValidateCreateLoading || isBindSaving || isDeleteStoreAccountLoading;

    const resetBindConfirmState = useCallback(() => {
        setIsBindConfirmOpen(false);
        setPendingBindValues(null);
        setBindConfirmDetachedApps([]);
    }, []);

    useEffect(() => {
        if (!enabled || !autoOpen) {
            return;
        }

        setIsModalOpen(true);
        shouldStripAutoOpenQueryRef.current = true;
    }, [autoOpen, enabled]);

    useEffect(() => {
        if (!isModalOpen || currentStep !== AppstoreAccountStepEnum.apps || accountId === null) {
            linksHydratedRef.current = false;
            previousLinksSnapshotRef.current = [];
        }
    }, [accountId, currentStep, isModalOpen]);

    useEffect(() => {
        if (!isAppsStep || accountId === null || !isLinksFetched || linksHydratedRef.current) {
            return;
        }

        appsForm.setFieldsValue(mapStoreAccountLinksRowsToFormValues(storeAccountLinksRows));
        previousLinksSnapshotRef.current = storeAccountLinksRows.map((row) => ({
            ...row,
            apps: [...row.apps],
            linkApps: row.linkApps.map((app) => ({...app})),
        }));
        linksHydratedRef.current = true;
    }, [accountId, appsForm, isAppsStep, isLinksFetched, storeAccountLinksRows]);

    const handleOpen = useCallback(() => {
        if (!enabled) {
            return;
        }

        setIsEditMode(false);
        setCurrentStep(AppstoreAccountStepEnum.apiKey);
        setAccountId(null);
        apiKeyForm.resetFields();
        appsForm.resetFields();
        resetBindConfirmState();
        linksHydratedRef.current = false;
        previousLinksSnapshotRef.current = [];
        setIsModalOpen(true);
    }, [apiKeyForm, appsForm, enabled, resetBindConfirmState]);

    const handleOpenForEdit = useCallback(
        (storeAccountId: number) => {
            if (!enabled) {
                return;
            }

            setIsEditMode(true);
            setAccountId(storeAccountId);
            setCurrentStep(AppstoreAccountStepEnum.apps);
            resetBindConfirmState();
            linksHydratedRef.current = false;
            previousLinksSnapshotRef.current = [];
            apiKeyForm.resetFields();
            appsForm.resetFields();
            setIsModalOpen(true);
        },
        [apiKeyForm, appsForm, enabled, resetBindConfirmState]
    );

    /** Resets modal UI only; `bindAndFinish` already calls `onSuccess` after a successful save. */
    function resetAddAppstoreModalState() {
        setIsModalOpen(false);
        setIsEditMode(false);
        setCurrentStep(AppstoreAccountStepEnum.apiKey);
        setAccountId(null);
        apiKeyForm.resetFields();
        appsForm.resetFields();
        resetBindConfirmState();

        if (shouldStripAutoOpenQueryRef.current) {
            onAutoOpen?.();
            shouldStripAutoOpenQueryRef.current = false;
        }
    }

    function handleClose() {
        resetAddAppstoreModalState();
        onSuccess();
    }

    function handleBindConfirmCancel() {
        resetBindConfirmState();
        onSuccess();
    }

    async function executeBind(values: AppstoreAppsFormType) {
        if (accountId === null) {
            return;
        }

        await bindAndFinish({
            accountId,
            ...(isEditMode
                ? {
                      failTitleKey: 'SOURCE_SETTINGS__ACCOUNTS__EDIT_FAIL_TITLE' as const,
                      successTitleKey: 'SOURCE_SETTINGS__ACCOUNTS__EDIT_SUCCESS_TITLE' as const,
                  }
                : {}),
            onComplete: () => {
                appsForm.resetFields();
                resetAddAppstoreModalState();
            },
            onSuccess,
            values,
        });
    }

    async function handleApiKeyFinish(values: AppstoreApiKeyFormType) {
        const newAccountId = await validateAndCreate(catalog, values, apiKeyForm);

        if (newAccountId !== null) {
            setIsEditMode(false);
            setAccountId(newAccountId);
            setCurrentStep(AppstoreAccountStepEnum.apps);
        }
    }

    async function handleAppsFinish(values: AppstoreAppsFormType) {
        if (accountId === null) {
            return;
        }

        if ((isEditMode && !isLinksFetched) || (!isEditMode && isStoreAccountAppsLoading)) {
            message.warning(getLocalizedString('SOURCE_SETTINGS__ACCOUNTS__APPSTORE__BIND_CONFIRM__WAIT_APPS'));

            return;
        }

        const selectedIds = collectSelectedAppIdsFromForm(values);
        const detached = getDetachedLinkedAppsFromLinks(previousLinksSnapshotRef.current, selectedIds);

        if (detached.length === 0) {
            await executeBind(values);

            return;
        }

        setPendingBindValues(values);
        setBindConfirmDetachedApps(detached);
        setIsBindConfirmOpen(true);
    }

    async function handleBindConfirmSave() {
        if (pendingBindValues === null) {
            return;
        }

        await executeBind(pendingBindValues);
    }

    async function submitDeleteStoreAccount(storeAccountId: number): Promise<boolean> {
        try {
            await deleteStoreAccountAsync({accountId: storeAccountId});

            await invalidateStoreAccountsQueries(queryClient);

            snackbar.success({
                description: (
                    <Locale
                        stringKey="SOURCE_SETTINGS__ACCOUNTS__DELETE_SUCCESS_TEXT"
                        valueMap={{catalogName: getCatalogName(catalog.id)}}
                    />
                ),
                message: <Locale stringKey="SOURCE_SETTINGS__ACCOUNTS__DELETE_SUCCESS_TITLE" />,
            });
            resetAddAppstoreModalState();
            onSuccess();

            return true;
        } catch {
            snackbar.error({
                description: <Locale stringKey="SOURCE_SETTINGS__ACCOUNTS__DELETE_FAIL_TEXT" />,
                message: <Locale stringKey="SOURCE_SETTINGS__ACCOUNTS__DELETE_FAIL_TITLE" />,
            });

            return false;
        }
    }

    function handleDeleteAccount(): void {
        if (accountId === null) {
            return;
        }

        const storeAccountId = accountId;

        modal.confirm({
            cancelText: <Locale stringKey="POPUP__BUTTON__CANCEL" />,
            content: (
                <p className={removeAccountStyles.RemoveAccount_content}>
                    <Locale stringKey="SOURCE_SETTINGS__ACCOUNTS__DELETE_MODAL_TEXT" valueMap={{companyName}} />
                </p>
            ),
            icon: (
                <FontAwesomeIcon
                    className={classNames('anticon', removeAccountStyles.RemoveAccount_icon)}
                    icon={faTriangleExclamation}
                />
            ),
            maskClosable: true,
            okButtonProps: {type: 'primary'},
            okText: <Locale stringKey="BUTTON__DELETE" />,
            okType: 'danger',
            onOk: () => submitDeleteStoreAccount(storeAccountId),
            title: <Locale stringKey="SOURCE_SETTINGS__ACCOUNTS__DELETE_MODAL_TITLE" />,
        });
    }

    return {
        accountId,
        apiKeyForm,
        appsForm,
        appsFormInitialValues,
        bindConfirmDetachedApps,
        currentStep,
        handleApiKeyFinish,
        handleAppsFinish,
        handleBindConfirmCancel,
        handleBindConfirmSave,
        handleClose,
        handleDeleteAccount,
        handleOpen,
        handleOpenForEdit,
        isBindConfirmOpen,
        isBindSaving,
        isEditMode,
        isInProgress,
        isModalOpen,
        isPrefillLoading,
        isStoreAccountAppsLoading,
        isStoreAccountLinksLoading,
        isApiKeyStepSubmittable,
        isAppsStepSubmittable,
        linkExtraAppOptions,
    };
}

/* eslint-enable complexity, max-statements, sonarjs/cognitive-complexity */
